import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import { buttonReset, focusRing } from '../../theme/focusStyles';

/* A real <button role='switch'> rather than a div: that is what makes it reachable by Tab
   and activatable by Enter and Space with no key handler of our own. buttonReset strips the
   native chrome so it renders exactly as it did as a div. */
const Container = styled('button')`
	${buttonReset}
	width: 50px;
	background-color: ${(props) => (props.isSelected ? '#19AB27' : '#4D4D4D')};
	cursor: pointer;
	user-select: none;
	border-radius: 30px;
	height: 24px;
	position: relative;
	display: flex;
	align-items: center;
	transition: all 0.3s ease;
	${focusRing}

	.dialog-button {
		line-height: 16px;
		cursor: pointer;
		background-color: #FAFAFA;
		width: 20px;
		height: 20px;
		border-radius: 50%;
		box-sizing: border-box;
		position: absolute;
		left: 28px;
		transition: all 0.3s ease;
	}

	.disabled {
		left: 2px;
	}
`;

/**
 *
 * @param {Function} onChange function called when the toggle is switched
 * @param {Boolean} checked determines the current state of the toggle
 * (flipped or not flipped)
 * @returns a custom toggle component that's more performant than the NPM
 * react-toggle component
 */

const TBToggleSwitch = ({ onChange, checked, ariaLabel }) => {
  // default state of the toggle is determined by this boolean
  const [selected, setSelected] = useState(checked);
  // fortification to prevent toggle from being flipped when it's loaded in
  const [firstLoad, setFirstLoad] = useState(true);

  /**
   * fires when toggle is flipped.  Waits .2 seconds before firing
   * dispatch so as to allow the animation to complete before the state updates
   */

  useEffect(() => {
    if (!firstLoad) {
      setTimeout(() => {
        onChange();
      }, 200);
    }
  }, [selected]);

  /**
   * fires once when the component loads and sets firstLoad to false
   * so that the above useEffect can do its work when toggle is flipped
   */
  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
    }
  }, []);

  return (
    <Container
      aria-checked={selected}
      aria-label={ariaLabel}
      isSelected={selected}
      onClick={() => {
        setSelected(!checked);
      }}
      role='switch'
      type='button'
    >
      {/* The moving knob. Purely decorative -- the button itself carries the semantics. */}
      <div className={`dialog-button ${selected ? 'enabled' : 'disabled'}`} />
    </Container>
  );
};

TBToggleSwitch.propTypes = {
  onChange: PropTypes.func.isRequired,
  checked: PropTypes.bool.isRequired,
  /** Names the switch for assistive tech; its visible label is usually an unlinked sibling. */
  ariaLabel: PropTypes.string,
};

TBToggleSwitch.defaultProps = {
  ariaLabel: undefined,
};

export default TBToggleSwitch;
